import { useEffect, useState } from "react";
import { Tag, X, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export function ChatTags({ active, onChanged }) {
  const [allTags, setAllTags] = useState([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const tags = active?.tags || [];
  const inRubrica = !!active?.contact_id;

  useEffect(() => {
    if (!open) return;
    api.get("/contacts/tags").then((r) => setAllTags(r.data || [])).catch(() => setAllTags([]));
  }, [open]);

  const save = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/chat/${encodeURIComponent(active.phone)}/tags`, { tags: next });
      onChanged?.(data.tags);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Tag non salvati");
    } finally {
      setSaving(false);
    }
  };
  const add = (t) => {
    const v = (t || "").trim();
    if (!v || tags.includes(v)) { setQuery(""); return; }
    save([...tags, v]);
    setQuery("");
  };
  const remove = (t) => save(tags.filter((x) => x !== t));

  if (!active) return null;
  const q = query.trim().toLowerCase();
  const suggestions = allTags.filter((t) => !tags.includes(t) && (!q || t.toLowerCase().includes(q)));
  const canCreate = q && !allTags.some((t) => t.toLowerCase() === q) && !tags.some((t) => t.toLowerCase() === q);

  return (
    <div className="flex items-center gap-1 flex-wrap min-w-0" data-testid="chat-tags">
      {tags.map((t) => (
        <span key={t} data-testid={`chat-tag-${t}`} className="inline-flex items-center gap-1 rounded-full bg-[#10B981]/15 text-[#10B981] px-2 py-0.5 text-[10px] font-medium max-w-[140px]">
          <span className="truncate">{t}</span>
          <button type="button" onClick={() => remove(t)} disabled={saving} data-testid={`chat-tag-remove-${t}`} title="Rimuovi tag"
                  className="hover:text-[#EF4444] transition-colors shrink-0"><X className="h-3 w-3" /></button>
        </span>
      ))}
      {inRubrica && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" data-testid="chat-tag-add-button" title="Aggiungi tag"
                    className="inline-flex items-center gap-1 rounded-full border border-dashed border-[#334155] text-slate-400 hover:text-[#10B981] hover:border-[#10B981] px-2 py-0.5 text-[10px] transition-colors">
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Tag
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64 p-2 bg-[#111827] border-[#1F2937] text-slate-100" data-testid="chat-tag-popover">
            <Input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} data-testid="chat-tag-input"
                   onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(suggestions[0] && !canCreate ? suggestions[0] : query); } }}
                   placeholder="Cerca o crea un tag…" className="h-8 bg-[#0A0E17] border-[#1F2937] text-slate-100 text-sm" />
            <div className="mt-2 max-h-48 overflow-y-auto space-y-0.5">
              {canCreate && (
                <button type="button" onClick={() => add(query)} data-testid="chat-tag-create"
                        className="w-full text-left px-2 py-1.5 rounded text-xs text-[#10B981] hover:bg-[#1F2937] flex items-center gap-1.5">
                  <Plus className="h-3 w-3" /> Crea «{query.trim()}»
                </button>
              )}
              {suggestions.map((t) => (
                <button key={t} type="button" onClick={() => add(t)} data-testid={`chat-tag-option-${t}`}
                        className="w-full text-left px-2 py-1.5 rounded text-xs text-slate-200 hover:bg-[#1F2937] flex items-center gap-1.5">
                  <Tag className="h-3 w-3 text-slate-500" /> {t}
                </button>
              ))}
              {!suggestions.length && !canCreate && <p className="text-[11px] text-slate-500 px-2 py-1.5">{allTags.length ? "Tutti i tag sono già assegnati." : "Nessun tag: scrivi un nome per crearne uno."}</p>}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
